import { useEffect, useState } from "react";
import { cx } from "@/utils/cx";
import { type RegionOption, regionCode, regionName, toggleRegion } from "../lib/regions";

type RegionFilterProps = {
  options: RegionOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
};

const PANEL_WIDTH = 240;

const Chevron = ({ open }: { open: boolean }) => (
  <svg
    viewBox="0 0 12 12"
    width={12}
    height={12}
    aria-hidden="true"
    style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .15s ease" }}
  >
    <path
      d="M3 4.5l3 3 3-3"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const singleRegionCopy = (options: RegionOption[]) => {
  const only = options[0];
  const name = only ? regionName(only.key) : "one region";
  const count = only?.merchantCount ?? 0;
  const lead = count === 1 ? "Your only merchant is" : `All ${count} merchants are`;
  return `${lead} in ${name}. Filtering by region unlocks when you add a merchant in a second region.`;
};

export function RegionFilter({ options, selected, onChange }: RegionFilterProps) {
  const [anchor, setAnchor] = useState<{ left: number; top: number } | null>(null);
  const [tooltip, setTooltip] = useState(false);
  const singleRegion = options.length < 2;
  const total = options.reduce((sum, option) => sum + option.merchantCount, 0);
  const matching = options
    .filter((option) => selected.includes(option.key))
    .reduce((sum, option) => sum + option.merchantCount, 0);

  useEffect(() => {
    if (!anchor) return;
    const close = () => setAnchor(null);
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      window.removeEventListener("keydown", onKey);
    };
  }, [anchor]);

  const label = selected.length ? `${selected.map(regionCode).join(", ")} · ${matching} of ${total}` : "All regions";

  const pillTone = (() => {
    if (singleRegion) return "cursor-default border-gray-200 bg-white text-[#21133a]/40";
    if (selected.length) return "cursor-pointer border-[#21133a] bg-[#21133a] text-white shadow-xs";
    return "cursor-pointer border-gray-200 bg-white text-[#21133a] shadow-xs hover:bg-gray-50";
  })();

  return (
    <div
      className="relative"
      tabIndex={singleRegion ? 0 : undefined}
      aria-describedby={singleRegion ? "overview-region-tip" : undefined}
      onMouseEnter={() => singleRegion && setTooltip(true)}
      onMouseLeave={() => setTooltip(false)}
      onFocus={() => singleRegion && setTooltip(true)}
      onBlur={() => setTooltip(false)}
      onKeyDown={(event) => {
        if (event.key === "Escape") setTooltip(false);
      }}
    >
      {singleRegion && tooltip && (
        <div
          id="overview-region-tip"
          role="tooltip"
          className="absolute top-[calc(100%+8px)] right-0 z-40 w-[248px] rounded-lg bg-[#21133a] px-3 py-2.5 text-left text-white shadow-lg"
        >
          <p className="text-[13px] leading-[18px] font-semibold">Only one region so far</p>
          <p className="mt-1 text-[12px] leading-4 text-white/75">{singleRegionCopy(options)}</p>
        </div>
      )}

      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={!!anchor}
        disabled={singleRegion}
        onClick={(event) => {
          const rect = event.currentTarget.getBoundingClientRect();
          setAnchor((current) =>
            current ? null : { left: Math.max(8, rect.right - PANEL_WIDTH), top: rect.bottom + 8 },
          );
        }}
        className={cx(
          "inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[13px] font-medium transition-colors duration-150",
          pillTone,
        )}
      >
        {label}
        <Chevron open={!!anchor} />
      </button>

      {anchor && (
        <>
          <div aria-hidden="true" onClick={() => setAnchor(null)} className="fixed inset-0 z-20" />
          <div
            role="group"
            aria-label="Filter by region"
            style={{ left: anchor.left, top: anchor.top, width: PANEL_WIDTH }}
            className="fixed z-40 flex flex-col rounded-xl border border-gray-200 bg-white p-2 shadow-lg"
          >
            {options.map((option) => (
              <label
                key={option.key}
                className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] text-[#21133a] hover:bg-gray-50"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(option.key)}
                  onChange={() => onChange(toggleRegion(selected, option.key))}
                  className="size-[15px] shrink-0 accent-[#21133a]"
                />
                <span className="min-w-0 flex-1 truncate">{regionName(option.key)}</span>
                <span className="text-[#21133a]/60 tabular-nums">{option.merchantCount}</span>
              </label>
            ))}
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="mt-1 cursor-pointer rounded-lg border-t border-[#21133a]/10 px-2.5 py-2 text-left text-[13px] font-medium text-[#21133a]/70 hover:text-[#21133a]"
              >
                Clear
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
